/**
 * Environment Configuration
 * Centralizes all environment variable management for CARBOwaste BDO
 */

export interface AppConfig {
  // Environment
  env: "development" | "test" | "production";
  nodeEnv: string;

  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
  };

  // Azure Configuration
  azure: {
    tenantId: string;
    clientId: string;
    subscriptionId: string;
  };

  // Application Insights
  appInsights: {
    instrumentationKey: string;
    connectionString: string;
  };

  // Feature Flags
  features: {
    enableDebugMode: boolean;
    enableTelemetry: boolean;
    enableBetaFeatures: boolean;
  };

  // Office Add-in Configuration
  officeAddin: {
    id: string;
    manifestUrl: string;
  };

  // Logging
  logging: {
    level: "debug" | "info" | "warn" | "error";
    enableConsoleLogs: boolean;
  };
}

/**
 * Get environment variable with fallback
 */
const getEnvVar = (key: string, defaultValue: string = ""): string => {
  // eslint-disable-next-line no-undef
  if (typeof process !== "undefined" && process.env) {
    // eslint-disable-next-line no-undef
    return process.env[key] || defaultValue;
  }
  return defaultValue;
};

/**
 * Parse boolean environment variable
 */
const parseBooleanEnv = (key: string, defaultValue: boolean = false): boolean => {
  const value = getEnvVar(key);
  if (value === "") return defaultValue;
  return value.toLowerCase() === "true";
};

/**
 * Parse number environment variable
 */
const parseNumberEnv = (key: string, defaultValue: number): number => {
  const value = getEnvVar(key);
  if (value === "") return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Application Configuration
 * Loads configuration from environment variables with type safety
 */
export const config: AppConfig = {
  env: getEnvVar("REACT_APP_ENV", "development") as AppConfig["env"],
  nodeEnv: getEnvVar("NODE_ENV", "development"),

  api: {
    baseUrl: getEnvVar("REACT_APP_API_BASE_URL", "https://api-carbowaste-prod.azurewebsites.net"),
    timeout: parseNumberEnv("REACT_APP_API_TIMEOUT", 30000),
  },

  azure: {
    tenantId: getEnvVar("REACT_APP_AZURE_TENANT_ID"),
    clientId: getEnvVar("REACT_APP_AZURE_CLIENT_ID"),
    subscriptionId: getEnvVar("REACT_APP_AZURE_SUBSCRIPTION_ID"),
  },

  appInsights: {
    instrumentationKey: getEnvVar("REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY"),
    connectionString: getEnvVar("REACT_APP_APPINSIGHTS_CONNECTION_STRING"),
  },

  features: {
    enableDebugMode: parseBooleanEnv("REACT_APP_ENABLE_DEBUG_MODE", true),
    enableTelemetry: parseBooleanEnv("REACT_APP_ENABLE_TELEMETRY", false),
    enableBetaFeatures: parseBooleanEnv("REACT_APP_ENABLE_BETA_FEATURES", false),
  },

  officeAddin: {
    id: getEnvVar("REACT_APP_OFFICE_ADDIN_ID", "carbowaste-bdo-local"),
    manifestUrl: getEnvVar("REACT_APP_MANIFEST_URL", "https://localhost:3000/manifest-local.xml"),
  },

  logging: {
    level: getEnvVar("REACT_APP_LOG_LEVEL", "debug") as AppConfig["logging"]["level"],
    enableConsoleLogs: parseBooleanEnv("REACT_APP_ENABLE_CONSOLE_LOGS", true),
  },
};

/**
 * Validates configuration on application startup
 */
export const validateConfig = (): void => {
  // eslint-disable-next-line no-undef
  console.log("🔧 Environment Configuration:", {
    env: config.env,
    nodeEnv: config.nodeEnv,
    apiBaseUrl: config.api.baseUrl,
    debugMode: config.features.enableDebugMode,
    officeAddinId: config.officeAddin.id,
  });
  
  // Debug: Log raw process.env value
  // eslint-disable-next-line no-undef
  if (typeof process !== "undefined" && process.env) {
    // eslint-disable-next-line no-undef
    console.log("🔍 DEBUG - Raw REACT_APP_API_BASE_URL from process.env:", process.env.REACT_APP_API_BASE_URL);
    // eslint-disable-next-line no-undef
    console.log("🔍 DEBUG - All process.env keys:", Object.keys(process.env).filter(k => k.startsWith('REACT_APP_')));
  }

  // Validate required configuration for production
  if (config.env === "production") {
    const requiredFields = [
      { field: "azure.tenantId", value: config.azure.tenantId },
      { field: "azure.clientId", value: config.azure.clientId },
      { field: "appInsights.connectionString", value: config.appInsights.connectionString },
    ];

    const missingFields = requiredFields.filter(({ value }) => !value);

    if (missingFields.length > 0) {
      // eslint-disable-next-line no-undef
      console.error(
        "❌ Missing required production configuration:",
        missingFields.map(({ field }) => field)
      );
      throw new Error(
        `Missing required production configuration: ${missingFields.map(({ field }) => field).join(", ")}`
      );
    }
  }
};

/**
 * Development helper to check configuration
 */
export const logConfiguration = (): void => {
  if (config.features.enableDebugMode && config.logging.enableConsoleLogs) {
    // eslint-disable-next-line no-undef
    console.group("🔧 CARBOwaste BDO Configuration");
    // eslint-disable-next-line no-undef
    console.table({
      Environment: config.env,
      "Node Environment": config.nodeEnv,
      "API Base URL": config.api.baseUrl,
      "API Timeout": config.api.timeout,
      "Debug Mode": config.features.enableDebugMode,
      Telemetry: config.features.enableTelemetry,
      "Beta Features": config.features.enableBetaFeatures,
      "Office Add-in ID": config.officeAddin.id,
      "Log Level": config.logging.level,
    });
    // eslint-disable-next-line no-undef
    console.groupEnd();
  }
};

export default config;
